import React, { useState } from "react";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Reorderable list with HTML5 drag + ↑/↓ fallback.
 * `items` is any array; `onChange` receives the reordered array.
 */
export default function DragList({
  items = [],
  onChange,
  renderItem,
  onAdd,
  addLabel = "Add item",
  emptyText = "No items yet.",
  getKey = (_, i) => i,
}) {
  const [dragIndex, setDragIndex] = useState(null);

  const moveTo = (from, to) => {
    if (from === to || from < 0 || to < 0 || to >= items.length) return;
    const next = [...items];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    onChange(next);
  };

  const removeAt = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <p className="text-xs text-white/40 py-4 text-center rounded-lg border border-dashed border-white/10">{emptyText}</p>
      )}
      {items.map((item, idx) => (
        <div
          key={getKey(item, idx)}
          draggable
          onDragStart={() => setDragIndex(idx)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => { if (dragIndex != null) moveTo(dragIndex, idx); setDragIndex(null); }}
          onDragEnd={() => setDragIndex(null)}
          className={`group flex items-start gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-2 transition-all ${
            dragIndex === idx ? "opacity-40 scale-[0.99]" : "hover:border-white/20 hover:bg-white/[0.05]"
          }`}
        >
          <button
            type="button"
            className="mt-1.5 p-1 text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing shrink-0"
            aria-label="Drag to reorder"
            tabIndex={-1}
          >
            <Icons.GripVertical className="w-3.5 h-3.5" />
          </button>
          <div className="flex-1 min-w-0">
            {renderItem(item, idx, {
              update: (patch) => {
                const next = [...items];
                next[idx] = typeof patch === "function" ? patch(item) : { ...item, ...patch };
                // string items: patch is the new string value
                if (typeof item === "string") next[idx] = typeof patch === "string" ? patch : patch;
                onChange(next);
              },
              updateValue: (value) => {
                const next = [...items];
                next[idx] = value;
                onChange(next);
              },
            })}
          </div>
          <div className="flex flex-col gap-0.5 shrink-0 opacity-60 group-hover:opacity-100">
            <button type="button" disabled={idx === 0} onClick={() => moveTo(idx, idx - 1)} className="p-1 text-white/40 hover:text-white disabled:opacity-20" aria-label="Move up">
              <Icons.ChevronUp className="w-3 h-3" />
            </button>
            <button type="button" disabled={idx === items.length - 1} onClick={() => moveTo(idx, idx + 1)} className="p-1 text-white/40 hover:text-white disabled:opacity-20" aria-label="Move down">
              <Icons.ChevronDown className="w-3 h-3" />
            </button>
            <button type="button" onClick={() => removeAt(idx)} className="p-1 text-white/40 hover:text-red-300" aria-label="Remove">
              <Icons.Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      ))}
      {onAdd && (
        <Button type="button" variant="ghost" size="sm" onClick={onAdd} className="w-full h-9 text-white/70 border border-dashed border-white/15 hover:border-cyan-400/30 hover:text-white hover:bg-cyan-400/5">
          <Icons.Plus className="w-3.5 h-3.5 mr-1.5" /> {addLabel}
        </Button>
      )}
    </div>
  );
}
